<script lang="ts" setup>
import { onBeforeMount, ref, UnwrapRef } from "vue";
import { showFailToast } from "vant";
import { useMoment } from "@/stores/moment";
import { useRoute, useRouter } from "vue-router";
import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import ReplyInput, {
  ReplyInputInstance,
} from "@/components/mobile/reply/ReplyInput.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import i18n from "@/locales";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { ReplyEntityType } from "@/enums";
import { Moment, Reply } from "@/types/entity";
import { useMate } from "@/stores/mate";
// 状态
const momentStore = useMoment();
const route = useRoute();
const router = useRouter();
const moment = ref<Moment>();
const mateStore = useMate();
// 底部输入框状态
const replyTarget = ref<Reply>(); // 底部输入框的回复目标
let defaultReply: Reply | undefined;
const replyInputRef = ref<ReplyInputInstance>();

export interface ReplyTree extends Reply {
  replyTo?: Reply; // parentReply
  children: ReplyTree[];
}

const enableActions = ref(true);
// Lifecycle
const isMomentLoading = ref(false);

onBeforeMount(async () => {
  try {
    isMomentLoading.value = true;
    const uuid = route.params.uuid.toString();
    const { from } = route.query;
    if (from === "requestList") {
      enableActions.value = false;
      moment.value = await momentStore.getOrFetchMomentItem(uuid, false);
    } else {
      moment.value = await momentStore.getOrFetchMomentItem(uuid, true);
    }
    if (moment.value?.type === 2)
      mateStore.changeCurrentMate(moment.value.mate.id);
  } catch (e) {
    showFailToast(i18n.global.t("Message.dynamicDisappearedInto"));
  } finally {
    isMomentLoading.value = false;
  }

  defaultReply = {
    ...(moment.value!.reply || {}),
    id: moment.value!.replyId,
    mate: moment.value!.mate,
  };
  replyTarget.value = defaultReply;
});

// Events Handler
const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
// Action Popup
const showActions = ref(false);
const toOperateMoment = ref<Moment | null>(null);
const toOperateReply = ref<Reply | null>(null);
const onReplyActionClick = (reply: Reply) => {
  toOperateMoment.value = null;
  toOperateReply.value = reply;
  showActions.value = true;
};
const onMomentActionClick = (moment: UnwrapRef<Moment>) => {
  toOperateReply.value = null;
  toOperateMoment.value = moment;
  showActions.value = true;
};

const afterDelete = () => {
  sessionStorageSet("momentDeleted", moment.value?.uuid);
  router.back();
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <button
          class="btn btn-ghost btn-square"
          @click="onMomentActionClick(moment!)"
        >
          <i class="iconify mingcute--more-1-line" />
        </button>
      </template>
    </ThreadsNav>
    <div
      v-if="isMomentLoading || !moment"
      class="flex items-center justify-center"
    >
      <span class="loading loading-ring"></span>
    </div>
    <div v-else class="w-full overflow-y-auto relative px-4 pt-2">
      <!-- 当前Thread -->
      <div class="flex flex-col gap-2">
        <TreeholeThreadItem
          :key="moment.uuid"
          :moment="moment"
          enable-audio-play
          enable-show-more-link
          :show-reply-list="false"
          :show-interaction-area="false"
          @reaction-click="onMomentActionClick(moment)"
          isTreehole
        />
        <div class="flex items-center gap-1">
          <i class="iconify mingcute--comment-line" />
          {{ moment.reply?.replyCount }}
        </div>
        <TreeholeReplyList
          :moment="moment"
          :root-id="moment?.replyId"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
          :is-treehole="moment.type === 2"
        />
      </div>
    </div>
    <!-- 底部输入框 -->
    <reply-input
      v-if="moment && enableActions"
      v-show="defaultReply?.id !== replyTarget?.id"
      ref="replyInputRef"
      :reply-target="replyTarget"
      :show-tip="defaultReply?.id !== replyTarget?.id"
      @reply-aborted="replyTarget = defaultReply"
      :entity-type="ReplyEntityType.Moment"
      :entity="moment"
    />
    <moment-actions
      v-model:show="showActions"
      :moment="toOperateMoment"
      :reply="toOperateReply"
      :replyMoment="moment"
      @after-delete="afterDelete"
    />
  </div>
</template>
